import { GoogleGenerativeAI } from '@google/generative-ai';
import { toast } from 'react-toastify';

class ShortAnswerService {
  constructor() {
    this.geminiClient = null;
    this.initializeService();
  }

  initializeService() {
    const geminiKey = process.env.REACT_APP_GEMINI_API_KEY;
    if (geminiKey) {
      try {
        this.geminiClient = new GoogleGenerativeAI(geminiKey);
        console.log('✅ Short Answer Service with Gemini initialized');
      } catch (error) {
        console.error('❌ Short Answer Service initialization failed:', error);
      }
    }
  }

  isAvailable() {
    return this.geminiClient !== null;
  }

  truncatePrompt(prompt, maxWords = 80) {
    if (!prompt) return '';
    const words = prompt.trim().split(/\s+/);
    if (words.length <= maxWords) return prompt;
    return words.slice(0, maxWords).join(' ') + '...';
  }

  async generateShortAnswerQuestions(concept, gradeLevel, language = 'English', count = 5) {
    if (!this.isAvailable()) {
      throw new Error('Short Answer Service not available. Set REACT_APP_GEMINI_API_KEY');
    }

    try {
      toast.info('✏️ Generating short answer questions...', { autoClose: 3000 });

      const truncatedConcept = this.truncatePrompt(concept, 80);
      const model = this.geminiClient.getGenerativeModel({ model: 'gemini-2.5-flash' });
      
      const prompt = `Generate ${count} short answer questions about "${truncatedConcept}" for Grade ${gradeLevel} students in ${language}.

CRITICAL: Return ONLY valid JSON with no additional text, markdown, or explanations.

Return this exact JSON structure:
{
  "topic": "${truncatedConcept}",
  "grade": ${gradeLevel},
  "language": "${language}",
  "questions": [
    {
      "id": 1,
      "question": "Question text here?",
      "expectedAnswer": "Sample answer showing what students should write",
      "markingScheme": "Key points to look for in answers",
      "marks": 5,
      "difficulty": "easy"
    }
  ]
}

Requirements:
- Age-appropriate for Grade ${gradeLevel}
- Clear questions in ${language}
- 2-3 sentence expected answers
- Varied difficulty: easy, medium, hard
- Marks: 2-10 based on difficulty
- Return ONLY valid JSON`;

      const response = await model.generateContent(prompt);
      let contentText = response.response.text().trim();

      contentText = contentText.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      contentText = contentText.replace(/^[^{]*{/, '{');
      contentText = contentText.replace(/}[^}]*$/, '}');
      
      let parsedContent;
      try {
        parsedContent = JSON.parse(contentText);
        
        if (!parsedContent.questions || !Array.isArray(parsedContent.questions)) {
          throw new Error('Invalid question structure');
        }
        
      } catch (jsonError) {
        console.warn('JSON parsing failed, creating fallback questions:', jsonError);
        
        parsedContent = {
          topic: truncatedConcept,
          grade: gradeLevel,
          language: language,
          questions: this.generateFallbackQuestions(truncatedConcept, count)
        };
      }

      const validatedQuestions = parsedContent.questions.map((q, index) => ({
        id: index + 1,
        question: q.question || `Explain the importance of ${truncatedConcept}.`,
        expectedAnswer: q.expectedAnswer || `${truncatedConcept} is important because it helps us understand various concepts and applications in our daily life.`,
        markingScheme: q.markingScheme || `Look for understanding of ${truncatedConcept}, examples, and clear explanation.`,
        marks: typeof q.marks === 'number' && q.marks > 0 ? q.marks : 5,
        difficulty: ['easy', 'medium', 'hard'].includes(q.difficulty) ? q.difficulty : 'medium'
      }));

      return {
        success: true,
        data: {
          topic: truncatedConcept,
          grade: gradeLevel,
          language: language,
          questions: validatedQuestions,
          totalQuestions: validatedQuestions.length,
          totalMarks: validatedQuestions.reduce((sum, q) => sum + q.marks, 0),
          model: 'gemini-2.5-flash',
          source: 'Short Answer Service',
          format: 'short_answer_questions'
        }
      };

    } catch (error) {
      console.error('Short answer generation failed:', error);
      
      return {
        success: true,
        data: {
          topic: concept,
          grade: gradeLevel,
          language: language,
          questions: this.generateFallbackQuestions(concept, count),
          totalQuestions: count,
          totalMarks: count * 5,
          model: 'fallback',
          source: 'Short Answer Service Fallback',
          format: 'short_answer_questions',
          note: 'Generated using fallback due to API issues'
        }
      };
    }
  }

  generateFallbackQuestions(concept, count) {
    const difficulties = ['easy', 'medium', 'hard'];
    const marks = [3, 5, 8];
    const fallbackQuestions = [];
    
    for (let i = 1; i <= count; i++) {
      const difficulty = difficulties[i % 3];
      const questionMarks = marks[i % 3];
      
      fallbackQuestions.push({
        id: i,
        question: `Explain the key features of ${concept}.`,
        expectedAnswer: `${concept} has several important characteristics that make it significant in its field. Students should mention the main aspects and provide examples.`,
        markingScheme: `Look for understanding of ${concept}, relevant examples, and clear explanation.`,
        marks: questionMarks,
        difficulty: difficulty
      });
    }
    return fallbackQuestions;
  }

  async generateFillInTheBlanks(concept, gradeLevel, language = 'English', count = 5) {
    if (!this.isAvailable()) {
      throw new Error('Short Answer Service not available. Set REACT_APP_GEMINI_API_KEY');
    }

    try {
      toast.info('📝 Generating fill-in-the-blanks...', { autoClose: 3000 });

      const truncatedConcept = this.truncatePrompt(concept, 80);
      const model = this.geminiClient.getGenerativeModel({ model: 'gemini-2.5-flash' });
      
      const prompt = `Generate ${count} fill-in-the-blank questions about "${truncatedConcept}" for Grade ${gradeLevel} students in ${language}.

CRITICAL: Return ONLY valid JSON with no additional text, markdown, or explanations.

Return this exact JSON structure:
{
  "topic": "${truncatedConcept}",
  "grade": ${gradeLevel},
  "language": "${language}",
  "fillInTheBlanks": [
    {
      "id": 1,
      "sentence": "The _______ is an important part of _______.",
      "answers": ["correct word 1", "correct word 2"],
      "marks": 2
    }
  ]
}

Requirements:
- Age-appropriate for Grade ${gradeLevel}
- Clear sentences in ${language}
- Use _______ for blanks
- Provide exact answers
- 1-2 marks per blank
- Return ONLY valid JSON`;

      const response = await model.generateContent(prompt);
      let contentText = response.response.text().trim();

      contentText = contentText.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      contentText = contentText.replace(/^[^{]*{/, '{');
      contentText = contentText.replace(/}[^}]*$/, '}');
      
      let parsedContent;
      try {
        parsedContent = JSON.parse(contentText);
        
        if (!parsedContent.fillInTheBlanks || !Array.isArray(parsedContent.fillInTheBlanks)) {
          throw new Error('Invalid fill-in-the-blanks structure');
        }
        
      } catch (jsonError) {
        console.warn('JSON parsing failed, creating fallback fill-in-the-blanks:', jsonError);
        
        parsedContent = {
          topic: truncatedConcept,
          grade: gradeLevel,
          language: language,
          fillInTheBlanks: this.generateFallbackFillInTheBlanks(truncatedConcept, count)
        };
      }

      const validatedFillInTheBlanks = parsedContent.fillInTheBlanks.map((q, index) => ({
        id: index + 1,
        sentence: q.sentence || `The _______ is related to ${truncatedConcept}.`,
        answers: Array.isArray(q.answers) ? q.answers : [truncatedConcept],
        marks: typeof q.marks === 'number' && q.marks > 0 ? q.marks : 2
      }));

      return {
        success: true,
        data: {
          topic: truncatedConcept,
          grade: gradeLevel,
          language: language,
          fillInTheBlanks: validatedFillInTheBlanks,
          totalQuestions: validatedFillInTheBlanks.length,
          totalMarks: validatedFillInTheBlanks.reduce((sum, q) => sum + q.marks, 0),
          model: 'gemini-2.5-flash',
          source: 'Short Answer Service - Fill in the Blanks',
          format: 'fill_in_the_blanks'
        }
      };

    } catch (error) {
      console.error('Fill-in-the-blanks generation failed:', error);
      throw new Error(`Fill-in-the-blanks generation failed: ${error.message}`);
    }
  }

  generateFallbackFillInTheBlanks(concept, count) {
    const fallbackQuestions = [];
    for (let i = 1; i <= count; i++) {
      fallbackQuestions.push({
        id: i,
        sentence: `The _______ is an important aspect of ${concept}.`,
        answers: ["concept", "feature"],
        marks: 2
      });
    }
    return fallbackQuestions;
  }

  async generateTrueFalse(concept, gradeLevel, language = 'English', count = 5) {
    if (!this.isAvailable()) {
      throw new Error('Short Answer Service not available. Set REACT_APP_GEMINI_API_KEY');
    }

    try {
      toast.info('✔️ Generating true/false questions...', { autoClose: 3000 });

      const truncatedConcept = this.truncatePrompt(concept, 80);
      const model = this.geminiClient.getGenerativeModel({ model: 'gemini-2.5-flash' });
      
      const prompt = `Generate ${count} true/false questions about "${truncatedConcept}" for Grade ${gradeLevel} students in ${language}.

CRITICAL: Return ONLY valid JSON with no additional text, markdown, or explanations.

Return this exact JSON structure:
{
  "topic": "${truncatedConcept}",
  "grade": ${gradeLevel},
  "language": "${language}",
  "trueFalse": [
    {
      "id": 1,
      "statement": "Statement about the topic",
      "answer": true,
      "explanation": "Why this is true or false",
      "marks": 1
    }
  ]
}

Requirements:
- Age-appropriate for Grade ${gradeLevel}
- Clear statements in ${language}
- Mix of true and false answers
- Brief explanations
- 1 mark each
- Return ONLY valid JSON`;

      const response = await model.generateContent(prompt);
      let contentText = response.response.text().trim();

      contentText = contentText.replace(/```json\n?/g, '').replace(/```\n?/g, '');
      contentText = contentText.replace(/^[^{]*{/, '{');
      contentText = contentText.replace(/}[^}]*$/, '}');
      
      let parsedContent;
      try {
        parsedContent = JSON.parse(contentText);
        
        if (!parsedContent.trueFalse || !Array.isArray(parsedContent.trueFalse)) {
          throw new Error('Invalid true/false structure');
        }
        
      } catch (jsonError) {
        console.warn('JSON parsing failed, creating fallback true/false:', jsonError);
        
        parsedContent = {
          topic: truncatedConcept,
          grade: gradeLevel,
          language: language,
          trueFalse: this.generateFallbackTrueFalse(truncatedConcept, count)
        };
      }

      const validatedTrueFalse = parsedContent.trueFalse.map((q, index) => ({
        id: index + 1,
        statement: q.statement || `${truncatedConcept} is an important topic.`,
        answer: typeof q.answer === 'boolean' ? q.answer : true,
        explanation: q.explanation || `This statement relates to ${truncatedConcept}`,
        marks: typeof q.marks === 'number' && q.marks > 0 ? q.marks : 1
      }));

      return {
        success: true,
        data: {
          topic: truncatedConcept,
          grade: gradeLevel,
          language: language,
          trueFalse: validatedTrueFalse,
          totalQuestions: validatedTrueFalse.length,
          totalMarks: validatedTrueFalse.reduce((sum, q) => sum + q.marks, 0),
          model: 'gemini-2.5-flash',
          source: 'Short Answer Service - True/False',
          format: 'true_false_questions'
        }
      };

    } catch (error) {
      console.error('True/false generation failed:', error);
      throw new Error(`True/false generation failed: ${error.message}`);
    }
  }

  generateFallbackTrueFalse(concept, count) {
    const fallbackQuestions = [];
    for (let i = 1; i <= count; i++) {
      fallbackQuestions.push({
        id: i,
        statement: `${concept} is an important topic to study.`,
        answer: i % 2 === 1, // Alternate true/false
        explanation: `This statement relates to the importance of ${concept}`,
        marks: 1
      });
    }
    return fallbackQuestions;
  }
}

export default ShortAnswerService;